import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { approvalsAPI, workflowsAPI } from '../api/client';
import { extractErrorMessage } from '../utils/errors';
import RejectModal from '../components/RejectModal';
import ApprovalProgressModal from '../components/ApprovalProgressModal';

const PURPOSE_LABELS = {
  initial: '🆕 Initial Approval',
  revision: '✏️ Revision',
  periodic_review: '🔄 Periodic Review',
};

function ApprovalsPage() {
  const [approvals, setApprovals] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterGroup, setFilterGroup] = useState('');
  const [rejectTarget, setRejectTarget] = useState(null); // the approval row being rejected
  const [progressTarget, setProgressTarget] = useState(null); // the approval row whose progress modal is open

  useEffect(() => {
    workflowsAPI.getGroups().then(res => setGroups(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchApprovals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterGroup]);

  const fetchApprovals = async () => {
    try {
      setLoading(true);
      const res = await approvalsAPI.getPending(filterGroup ? { group_id: filterGroup } : {});
      setApprovals(res.data);
    } catch (err) {
      console.error('Error fetching approvals:', err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (approval) => {
    try {
      // No member_id: the backend derives the acting member from the session.
      // A request does not get to nominate who it is acting as.
      await approvalsAPI.approve(approval.id, {});
      setError(null);
      fetchApprovals();
    } catch (err) {
      console.error('Error approving:', err);
      setError(extractErrorMessage(err));
    }
  };

  const handleRejectClick = (approval) => {
    setRejectTarget(approval);
  };

  const handleRejectSave = async (data) => {
    try {
      await approvalsAPI.reject(rejectTarget.id, data);
      setError(null);
      setRejectTarget(null);
      fetchApprovals();
    } catch (err) {
      console.error('Error rejecting:', err);
      setError(extractErrorMessage(err));
    }
  };

  if (loading) {
    return <div className="loading">Loading approvals...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h2>✅ Approvals</h2>
        <button className="btn btn-primary" onClick={fetchApprovals}>
          🔄 Refresh
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="card-header">
          <h3>Filter</h3>
        </div>
        <div className="form-group">
          <label>Approval Group</label>
          <select value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)}>
            <option value="">All Groups</option>
            {groups.map(g => (
              <option key={g.id} value={g.id}>{g.name} — {g.owner_node_name}</option>
            ))}
          </select>
        </div>
      </div>

      {approvals.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <h3>Nothing awaiting your approval</h3>
            <p>
              This queue shows gates held by committees you belong to — plus,
              while you are acting for someone else, theirs. Other documents may
              be in approval elsewhere.
            </p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <h3>Pending Approvals ({approvals.length})</h3>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Gate</th>
                  <th>Rule</th>
                  <th>Progress</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {approvals.map(approval => {
                  // approved_member_ids/viewer_has_approved are computed
                  // server-side by the same function (workflow_service.
                  // step_progress) approve_step calls to decide completion --
                  // never re-derived from actions_so_far here, which is
                  // deliberately widened to every cycle of this gate and would
                  // over-count after a send-back re-opened it (issue #221).
                  const approvedCount = approval.approved_member_ids.length;
                  return (
                    <tr key={approval.id}>
                      <td>
                        {/* state tells the document page to offer "Back to
                            Approvals" instead of "Back to Documents". Only the
                            breadcrumb depends on it -- the approve/reject bar
                            there asks the server, so it survives a reload
                            where this state does not. */}
                        <Link to={`/documents/${approval.document_id}`} state={{ from: '/approvals' }}>
                          <strong>{approval.document_title}</strong>
                        </Link>
                        {approval.document_hierarchy_display_path && (
                          <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                            {approval.document_hierarchy_display_path}
                          </div>
                        )}
                        <div style={{ fontSize: '12px', marginTop: '3px' }}>
                          <span className="badge" style={{ background: 'var(--neutral-bg)', color: 'var(--neutral-ink)' }}>
                            {PURPOSE_LABELS[approval.purpose] || approval.purpose}
                          </span>
                          {approval.type_evaluation_score != null && (
                            <span
                              className="badge"
                              style={{ marginLeft: '6px', background: 'var(--neutral-bg)', color: 'var(--neutral-ink)' }}
                              title="Document-type match score"
                            >
                              🏷️ {Math.round(approval.type_evaluation_score * 100)}%
                            </span>
                          )}
                          {approval.corpus_duplicate_count > 0 && (
                            <span
                              className="badge"
                              style={{ marginLeft: '6px', background: 'var(--warning-bg)', color: 'var(--warning-ink)' }}
                              title="Documents already in the library that closely resemble this one"
                            >
                              ⧉ {approval.corpus_duplicate_count} similar
                            </span>
                          )}
                          {approval.document_revision_id && (
                            // The READ-ONLY revision view, not the author's edit
                            // page -- an approver following this link must see
                            // exactly what they're signing off on (redline,
                            // attribution, comments) without being able to type
                            // into someone else's proposal.
                            <Link
                              to={`/documents/${approval.document_id}/revisions/${approval.document_revision_id}`}
                              state={{ from: '/approvals' }}
                              style={{ marginLeft: '8px' }}
                            >
                              View proposed changes
                            </Link>
                          )}
                        </div>
                      </td>
                      <td>
                        Gate {approval.step_order} of {approval.total_steps}: {approval.group_name}
                        {approval.group_owner_path && (
                          <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{approval.group_owner_path}</div>
                        )}
                        {/* Only present for a gate held through a delegation
                            (issue #44) -- an action here is recorded against
                            the person named, not the viewer. */}
                        {approval.acting_on_behalf_of_name && (
                          <div style={{ fontSize: '12px', color: 'var(--info-ink)' }}>
                            🤝 on behalf of {approval.acting_on_behalf_of_name}
                          </div>
                        )}
                        {/* Present when this row is still in the queue (an
                            ALL gate awaiting other members) after the viewer
                            has already acted on it -- otherwise the row reads
                            as unactioned. */}
                        {approval.viewer_has_approved && (
                          <div style={{ fontSize: '12px', color: 'var(--success-ink)' }}>✓ You approved</div>
                        )}
                        {approval.viewer_has_rejected && (
                          <div style={{ fontSize: '12px', color: 'var(--danger-ink)' }}>↩ You returned this</div>
                        )}
                      </td>
                      <td>
                        <span className="badge" style={{ background: 'var(--teal-500)', color: 'white' }}>
                          {approval.completion_rule === 'all' ? 'All must approve' : 'Any one approver'}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn-link"
                          onClick={() => setProgressTarget(approval)}
                        >
                          {approval.completion_rule === 'all'
                            ? `${approvedCount} of ${approval.members.length} approved`
                            : `Awaiting any approver — ${approvedCount} of ${approval.members.length} so far`}
                        </button>
                      </td>
                      <td>
                        <button
                          className="btn btn-success btn-small"
                          onClick={() => handleApprove(approval)}
                          style={{ marginRight: '5px' }}
                        >
                          ✓ Approve
                        </button>
                        <button
                          className="btn btn-danger btn-small"
                          onClick={() => handleRejectClick(approval)}
                        >
                          ✗ Reject
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rejectTarget && (
        <RejectModal
          approval={rejectTarget}
          onSave={handleRejectSave}
          onClose={() => setRejectTarget(null)}
        />
      )}

      {progressTarget && (
        <ApprovalProgressModal
          approval={progressTarget}
          onClose={() => setProgressTarget(null)}
        />
      )}
    </div>
  );
}

export default ApprovalsPage;
